import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import CustomToast from '../../components/CustomToast';
import {
  convertAmountToWords,
  fetchPaymentGatewayConfig,
  fetchPaymentHistory,
  fetchPaymentStatus,
  formatInr,
  startPayout,
  statusClass,
  statusLabel,
} from './paymentGatewayClient';
import './PaymentGateway.css';

const PaymentGatewayPayout = ({ user, bankDetails, hasPositions, availableBalance }) => {
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [lastTxn, setLastTxn] = useState(null);
  const statusNotified = useRef('');

  const loadConfig = useCallback(async () => {
    try {
      const data = await fetchPaymentGatewayConfig();
      setConfig(data);
    } catch (error) {
      setConfig({
        enabled: false,
        payoutEnabled: false,
        manualWithdrawEnabled: true,
      });
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const rows = await fetchPaymentHistory('payout', 8);
      setHistory(rows);
    } catch (error) {
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadHistory();
  }, [loadConfig, loadHistory]);

  const pendingTxnId =
    (lastTxn && ['pending', 'requested'].includes(lastTxn.status) && lastTxn.merchantTxnId) ||
    (history.find((row) => row && ['pending', 'requested'].includes(row.status) && row.merchantTxnId) || {}).merchantTxnId ||
    '';

  useEffect(() => {
    const pgTxn = String(pendingTxnId || '').trim();
    if (!pgTxn) return undefined;

    let cancelled = false;
    let tries = 0;

    const poll = async () => {
      let nextStatus = '';
      try {
        const txn = await fetchPaymentStatus(pgTxn);
        if (!cancelled && txn) {
          nextStatus = String(txn.status || '');
          setLastTxn(txn);
          if (txn.status === 'requested') {
            statusNotified.current = `${pgTxn}:requested`;
          } else if (txn.status === 'pending') {
            if (statusNotified.current === `${pgTxn}:requested`) {
              toast.success(
                <CustomToast title="Withdrawal approved" description="Amount deducted. The bank payout is being processed." />
              );
              loadHistory();
            }
            statusNotified.current = `${pgTxn}:pending`;
          } else if (txn.status === 'completed' || txn.status === 'failed' || txn.status === 'cancelled') {
            if (statusNotified.current !== `${pgTxn}:${txn.status}`) {
              statusNotified.current = `${pgTxn}:${txn.status}`;
              if (txn.status === 'completed') {
                toast.success(
                  <CustomToast title="Withdrawal sent" description={`${formatInr(txn.amount)} has been sent to your bank.`} />
                );
              } else if (txn.status === 'cancelled') {
                toast.error(
                  <CustomToast title="Withdrawal rejected" description="Your master did not approve this request. No amount was deducted." />
                );
              } else {
                toast.error(
                  <CustomToast title="Withdrawal failed" description="The amount will be reversed if it was deducted." />
                );
              }
            }
            loadHistory();
            return;
          }
        }
      } catch (error) {
        // keep polling; webhook or fetch may still complete
      }

      tries += 1;
      const waitingForMaster = nextStatus === 'requested';
      const maxTries = waitingForMaster ? 360 : 36;
      const delay = waitingForMaster ? 10000 : 5000;
      if (!cancelled && tries < maxTries) {
        window.setTimeout(poll, delay);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [pendingTxnId, loadHistory]);

  if (!config || !config.payoutEnabled) {
    return null;
  }

  const min = Number(config.minPayout || 100);
  const max = Number(config.maxPayout || 100000);
  const words = convertAmountToWords(amount);
  const accountReady = Boolean(bankDetails?.accountNumber && bankDetails?.ifsc && bankDetails?.accountHolderName);

  const handleWithdraw = async (event) => {
    event.preventDefault();
    if (hasPositions) {
      toast.error(
        <CustomToast title="Withdrawal blocked" description="Close open positions before withdrawing." />
      );
      return;
    }
    if (!accountReady) {
      toast.error(
        <CustomToast title="Bank details required" description="Save your bank account and IFSC first." />
      );
      return;
    }

    const value = parseInt(amount, 10);
    if (!Number.isInteger(value) || value < min || value > max) {
      toast.error(
        <CustomToast title="Invalid amount" description={`Enter a whole rupee amount between ${formatInr(min)} and ${formatInr(max)}.`} />
      );
      return;
    }

    if (Number(availableBalance) < value) {
      toast.error(
        <CustomToast title="Insufficient balance" description={`Available balance is ${formatInr(availableBalance)}.`} />
      );
      return;
    }

    setBusy(true);
    try {
      const txn = await startPayout({
        amount: value,
        name: bankDetails.accountHolderName || user?.clientName || user?.name,
        mobile: user?.phoneNumber || user?.phone,
        account: bankDetails.accountNumber,
        ifsc: bankDetails.ifsc || bankDetails.IFSC,
      });
      setLastTxn(txn);
      setAmount('');
      await loadHistory();
      toast.success(
        <CustomToast
          title="Withdrawal requested"
          description="Your master will review this request. Balance is deducted only after approval."
        />
      );
    } catch (error) {
      toast.error(
        <CustomToast title="Withdrawal not started" description={error.message || 'Please try again.'} />
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pg-card">
      <h3>Withdraw to bank</h3>
      {/* <p className="pg-sub">Request is sent to your master first. After approval, the amount is deducted and paid to your saved bank account.</p> */}

      {lastTxn && (
        <div className={`pg-banner pg-banner--${lastTxn.status === 'completed' ? 'ok' : lastTxn.status === 'failed' || lastTxn.status === 'cancelled' ? 'fail' : 'pending'}`}>
          Withdrawal {statusLabel(lastTxn.status).toLowerCase()} for {formatInr(lastTxn.amount)}. Ref {lastTxn.merchantTxnId}.
        </div>
      )}

      <form onSubmit={handleWithdraw}>
        <div className="pg-limits">
          <span>Min {formatInr(min)}</span>
          <span>Available {formatInr(availableBalance || 0)}</span>
        </div>
        <div className="pg-field">
          <label>Amount (INR)</label>
          <input
            type="number"
            min={min}
            max={max}
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={hasPositions || busy}
            required
          />
          {words ? <div className="pg-words">{words}</div> : null}
        </div>
        <div className="pg-field">
          <label>Beneficiary</label>
          <input
            type="text"
            value={bankDetails?.accountHolderName || ''}
            readOnly
            disabled
          />
        </div>
        <div className="pg-field">
          <label>Account / IFSC</label>
          <input
            type="text"
            value={accountReady ? `${bankDetails.accountNumber} / ${bankDetails.ifsc || bankDetails.IFSC}` : 'Add bank details below'}
            readOnly
            disabled
          />
        </div>
        <button type="submit" className="pg-btn pg-btn--payout" disabled={busy || hasPositions || !accountReady}>
          {busy ? 'Submitting…' : 'Withdraw now'}
        </button>
      </form>
      {/* <p className="pg-note">No money is taken until your master approves. After approval the payout goes to the gateway. Failed payouts are reversed automatically.</p> */}

      {history.length > 0 && (
        <div className="pg-history">
          <h4>Recent online withdrawals</h4>
          {history.map((row) => (
            <div className="pg-row" key={row.merchantTxnId}>
              <div>
                <div>{formatInr(row.amount)}</div>
                <div style={{ color: '#888', fontSize: '12px' }}>{row.merchantTxnId}</div>
              </div>
              <span className={`pg-status ${statusClass(row.status)}`}>{statusLabel(row.status)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentGatewayPayout;
